"use client";

import { useState } from "react";
import { MessageSquare, Eye, Pin, Globe, Lock, MoreHorizontal, Trash2, Flame, PartyPopper } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ReportContentDialog } from "@/components/report-content-dialog";
import { toast } from "@/components/ui/use-toast";
import { LoginPromptDialog } from "@/components/login-prompt-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-teal-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
];

function avatarColor(initial: string): string {
  const code = initial ? initial.charCodeAt(0) : 0;
  return AVATAR_COLORS[(code || 0) % AVATAR_COLORS.length];
}

interface ForumCardProps {
  id: string;
  title: string;
  author: string;
  authorCertification?: string | null;
  category: string;
  replies: number;
  views: number;
  likes: number;
  isLiked?: boolean;
  isPinned?: boolean;
  lastActivity: string;
  avatar: string;
  avatarUrl?: string | null;
  visibility?: "PUBLIC" | "CL_COMMUNITY";
  isRemoved?: boolean;
  isOfficial?: boolean;
  isAuthenticated?: boolean;
  isOwner?: boolean;
  onClick?: () => void;
  onLike?: (id: string, liked: boolean, newCount: number) => void;
  onDelete?: (id: string) => void;
  onCommentClick?: (e: React.MouseEvent) => void;
}

export default function ForumCard({
  id,
  title,
  author,
  authorCertification,
  category,
  replies,
  views,
  likes,
  isLiked = false,
  isPinned = false,
  lastActivity,
  avatar,
  avatarUrl,
  visibility = "PUBLIC",
  isRemoved,
  isOfficial = false,
  isAuthenticated = false,
  isOwner = false,
  onClick,
  onLike,
  onDelete,
  onCommentClick,
}: ForumCardProps): JSX.Element {
  const [localLiked, setLocalLiked] = useState(isLiked);
  const [localCount, setLocalCount] = useState(likes);
  const [liking, setLiking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const hideOfficialCounts = isOfficial && replies === 0;

  const handleCardClick = (e: React.MouseEvent): void => {
    const target = e.target as HTMLElement;
    // Skip navigation if the click came from any interactive child OR from
    // any Radix portal that's rendered outside the card in the DOM but is
    // still a React-tree descendant (Dialog, DropdownMenu, Popover, etc.).
    // React synthetic events bubble through the component tree even when
    // rendered in a portal, so without this guard, clicking inside the
    // Report dialog (textarea, buttons, etc.) would fire Card.onClick and
    // navigate away mid-interaction.
    if (
      target.closest('[data-dropdown]') ||
      target.closest('[data-like]') ||
      target.closest('[data-comments]') ||
      target.closest('[role="dialog"]') ||
      target.closest('[role="alertdialog"]') ||
      target.closest('[role="menu"]') ||
      target.closest('[role="listbox"]') ||
      target.closest('[data-radix-popper-content-wrapper]') ||
      target.closest('[data-radix-portal]')
    ) {
      return;
    }
    onClick?.();
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated || liking) return;
    setLiking(true);
    const wasLiked = localLiked;
    const newLiked = !wasLiked;
    const newCount = wasLiked ? localCount - 1 : localCount + 1;
    setLocalLiked(newLiked);
    setLocalCount(newCount);
    try {
      const res = await fetch(`/api/forums/topics/${id}/like`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        const liked = typeof data.liked === "boolean" ? data.liked : newLiked;
        const count = typeof data.likeCount === "number"
          ? (liked ? Math.max(data.likeCount, 1) : Math.max(0, data.likeCount))
          : Math.max(0, newCount);
        setLocalLiked(liked);
        setLocalCount(count);
        onLike?.(id, liked, count);
      } else {
        setLocalLiked(wasLiked);
        setLocalCount(localCount);
      }
    } catch {
      setLocalLiked(wasLiked);
      setLocalCount(localCount);
    } finally {
      setLiking(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOwner || deleting) return;
    if (!confirm("Delete this post? This action cannot be undone.")) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/forums/topics/${id}`, { method: "DELETE" });
      if (res.ok) {
        onDelete?.(id);
      } else {
        const data = await res.json().catch(() => ({}));
        toast({ title: "Couldn't delete post", description: data.error || "Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Couldn't delete post", description: "Please try again.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      className="hover:shadow-md transition-all duration-200 cursor-pointer border-l-[3px] border-l-primary overflow-hidden"
      onClick={handleCardClick}
    >
      <CardContent className="p-0">

        {/* ── Category strip ─────────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
          <div className="flex items-center gap-1.5">
            {isPinned && <Pin className="h-3 w-3 text-primary" />}
            <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {category}
            </span>
            {visibility === "CL_COMMUNITY" && (
              <Lock className="h-3 w-3 text-blue-600" />
            )}
            {/* Hot badge: high engagement */}
            {!hideOfficialCounts && (views > 100 || (replies >= 5 && views > 20)) && (
              <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 border border-amber-200">
                <Flame className="h-2.5 w-2.5" />
                Hot
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{lastActivity}</span>
        </div>

        {/* ── Title + author ──────────────────────────────────────── */}
        <div className="px-4 pt-3 pb-3">

          {/* Title row */}
          <div className="flex items-start gap-2 mb-2">
            {visibility === "PUBLIC" ? (
              <Globe className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
            ) : (
              <Lock className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            )}
            <h3 className="font-semibold hover:text-primary transition-colors line-clamp-2 flex-1 min-w-0">
              {isRemoved ? (
                <span className="italic text-red-500">[Post removed by moderation]</span>
              ) : (
                title
              )}
            </h3>
            <div data-dropdown className="shrink-0 -mt-1 -mr-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  // Stop React-event bubbling so clicks inside the
                  // portaled dropdown content never reach the Card's
                  // onClick (which would call router.push and navigate
                  // away — React events bubble through the component
                  // tree, not the DOM, so the portal does NOT isolate
                  // them on its own).
                  onClick={(e) => e.stopPropagation()}
                >
                  {isOwner && (
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDelete(e);
                      }}
                      disabled={deleting}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deleting ? "Deleting…" : "Delete post"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={(e) => e.stopPropagation()}
                    onSelect={() => {
                      // Defer opening the dialog until the dropdown has
                      // fully closed so Radix's body `pointer-events: none`
                      // doesn't leak onto the new dialog.
                      setTimeout(() => {
                        if (isAuthenticated) {
                          setReportOpen(true);
                        } else {
                          setLoginPromptOpen(true);
                        }
                      }, 0);
                    }}
                  >
                    Report Post
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <ReportContentDialog
              targetId={id}
              targetType="FORUM_POST"
              open={reportOpen}
              onOpenChange={setReportOpen}
            />
            <LoginPromptDialog
              open={loginPromptOpen}
              onOpenChange={setLoginPromptOpen}
            title="Login to report"
            description="You need to be logged in to report a post. Please log in or create an account to continue."
            />
          </div>

          {/* Author row */}
          <div className="flex items-center gap-2 mb-3">
            <Avatar className="h-5 w-5 shrink-0">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={avatar} />}
              <AvatarFallback className={`${avatarColor(avatar)} text-white font-semibold text-[9px]`}>
                {avatar}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-muted-foreground truncate">
              {author}
              {authorCertification && (
                <span className="text-primary font-medium"> · {authorCertification}</span>
              )}
            </span>
          </div>

          {/* Stats row — View · Like · Chat. Official starter threads hide
              counts until a real member replies so they don't look like
              fake activity. Buttons stay so the set matches every surface. */}
          <div className="flex items-center gap-1.5">
            {!hideOfficialCounts && (
              <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-gray-200 text-gray-500">
                <Eye className="h-3.5 w-3.5" />
                <span>{views}</span>
              </div>
            )}

            {/* Like */}
            <button
              data-like
              onClick={handleLike}
              disabled={!isAuthenticated || liking}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border disabled:opacity-50 ${
                localLiked
                  ? "bg-amber-50 border-amber-300 text-amber-600"
                  : isAuthenticated
                    ? "border-gray-200 text-gray-500 hover:bg-amber-50 hover:border-amber-200 hover:text-amber-600"
                    : "border-gray-200 text-gray-400 cursor-default"
              }`}
              title={isAuthenticated ? (localLiked ? "Unlike" : "Celebrate") : "Login to celebrate"}
            >
              <PartyPopper className="h-3.5 w-3.5" />
              {!hideOfficialCounts && <span>{localLiked ? Math.max(localCount, 1) : localCount}</span>}
            </button>

            {/* Chat */}
            {onCommentClick ? (
              <button
                data-comments
                onClick={onCommentClick}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                title="Show comments"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {!hideOfficialCounts && <span>{replies}</span>}
              </button>
            ) : (
              <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-gray-200 text-gray-500">
                <MessageSquare className="h-3.5 w-3.5" />
                {!hideOfficialCounts && <span>{replies}</span>}
              </div>
            )}
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
